import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
} from "react";

// Lets the unsaved-changes allocation editor warn on browser-level unload
// (refresh/close/typed URL) and on the Funder Allocation page's own nav
// (tab switch, back-to-list buttons). It can't catch the browser Back
// button - that needs a data router (createBrowserRouter), which this app
// doesn't use - so this covers the navigation this app's UI itself drives.
const DirtyGuardContext = createContext(null);

const DirtyGuardProvider = ({ children }) => {
    const dirtyRef = useRef(false);
    const messageRef = useRef(
        "You have unsaved changes. Leave without saving?",
    );

    const setDirty = useCallback((isDirty, message) => {
        dirtyRef.current = isDirty;
        if (message) messageRef.current = message;
    }, []);

    const confirmLeave = useCallback(() => {
        if (!dirtyRef.current) return true;
        const confirmed = window.confirm(messageRef.current);
        // Once the user has explicitly chosen to leave without saving,
        // the guard must stand down - otherwise it re-prompts on every
        // subsequent navigation, since nothing else ever clears this flag
        // (the component whose edits were discarded has already unmounted
        // by the time confirmLeave resolves, so it never gets a chance to
        // call setDirty(false) itself).
        if (confirmed) dirtyRef.current = false;
        return confirmed;
    }, []);

    useEffect(() => {
        const handler = (e) => {
            if (dirtyRef.current) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, []);

    return (
        <DirtyGuardContext.Provider value={{ setDirty, confirmLeave }}>
            {children}
        </DirtyGuardContext.Provider>
    );
};

const useDirtyGuard = () => useContext(DirtyGuardContext);

export { DirtyGuardProvider, useDirtyGuard };
